const oeSearchTermResolver = (term) => {
  const query = term.trim().toLocaleLowerCase('de');
  if (!query || query.includes(' ')) return term;
  const direct = { plle: 'pellet', plet: 'pellet', plett: 'pellet' };
  if (direct[query]) return direct[query];
  const terms = ['kamin', 'kaminofen', 'pellet', 'pelletofen', 'elektrokamin', 'schornstein', 'feuerschale', 'bioethanol', 'wodtke', 'austroflamm', 'thermorossi'];
  if (terms.some((candidate) => candidate === query || (query.length >= 3 && candidate.startsWith(query)))) return term;
  if (query.length < 4) return term;
  const oneEdit = (left, right) => {
    if (Math.abs(left.length - right.length) > 1) return false;
    if (left.length === right.length) {
      const misses = [...left].map((letter, index) => letter === right[index] ? -1 : index).filter((index) => index >= 0);
      return misses.length === 1 || (misses.length === 2 && misses[1] === misses[0] + 1 && left[misses[0]] === right[misses[1]] && left[misses[1]] === right[misses[0]]);
    }
    const [shorter, longer] = left.length < right.length ? [left, right] : [right, left];
    for (let index = 0; index < longer.length; index += 1) {
      if (longer.slice(0, index) + longer.slice(index + 1) === shorter) return true;
    }
    return false;
  };
  const candidates = terms.filter((candidate) => candidate[0] === query[0] && oneEdit(query, candidate));
  return candidates.length === 1 ? candidates[0] : term;
};
window.OeSearchTermResolver = { resolve: oeSearchTermResolver };

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedProductPages = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestId = 0;
    this.renderedRequestId = 0;
    this.pendingProductPage = null;
    this.pendingSupplementalProducts = null;
    this.deferredRequestProducts = [];
    this.productCatchup = false;
    this.isNativeSearch = this.hasAttribute('data-native-predictive-search');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.predictiveSearchResults.addEventListener('click', (event) => {
      const button = event.target.closest('[data-oe-search-more]');
      if (!button) return;
      const group = button.closest('.oe-mega-search__group');
      const expanded = group.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';
    });

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    if (this.isNativeSearch) {
      window.addEventListener('resize', () => {
        if (this.isOpen) this.updateOverlayPosition();
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.requestId += 1;
      this.removeAttribute('results');
      this.predictiveSearchResults.innerHTML = '';
      this.close(true);
      return;
    }

    const minimumCharacters = Math.max(1, parseInt(this.dataset.minChars || '1', 10));
    if (this.searchTerm.length < minimumCharacters) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.requestId += 1;
      if (!this.isNativeSearch) this.close();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    const resolved = oeSearchTermResolver(query);
    if (resolved !== query) this.input.value = resolved;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.requestId += 1;
      this.removeAttribute('results');
      this.predictiveSearchResults.innerHTML = '';
      this.closeResults(true);
    }
  }

  onFocus() {
    if (this.suppressFocusOpen) return;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      return;
    }
    if (currentSearchTerm.length < Math.max(1, parseInt(this.dataset.minChars || '1', 10))) {
      if (this.isNativeSearch) this.open();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (this.isNativeSearch) return;
    if (!this.getQuery().length && !this.isNativeSearch) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (this.isNativeSearch) return;
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const resolvedTerm = oeSearchTermResolver(searchTerm);
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestId;
    this.renderedRequestId = 0;
    this.pendingProductPage = null;
    this.pendingSupplementalProducts = null;
    this.deferredRequestProducts = [];
    this.productCatchup = false;
    this.productPageLoading = false;
    this.productScroll = null;
    const allowedResourceTypes = ['product', 'collection', 'article', 'page'];
    const resourceTypes = (this.dataset.resourceTypes || '')
      .split(',')
      .filter((type) => allowedResourceTypes.includes(type))
      .join(',');
    const configuredLimit = parseInt(this.dataset.resultsLimit || '', 10);
    const resultsLimit = Number.isNaN(configuredLimit) ? null : Math.min(10, Math.max(1, configuredLimit));
    const queryKey = `${searchTerm.replace(' ', '-').toLowerCase()}|${resourceTypes || 'default'}|${resultsLimit || 'default'}`;
    this.setLiveRegionLoadingState();
    if (this.isNativeSearch) this.getProductSearchResults(searchTerm, queryKey, requestId, 1);
    if (this.isNativeSearch && ['pell', 'pelletofen'].includes(searchTerm.trim().toLowerCase())) {
      this.loadSupplementalProducts(searchTerm, requestId);
    }

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.renderedRequestId = requestId;
      this.renderPendingProductPage(requestId);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const predictiveSearchUrl = new URL(routes.predictive_search_url, window.location.origin);
    predictiveSearchUrl.searchParams.set('q', resolvedTerm);
    predictiveSearchUrl.searchParams.set('section_id', 'predictive-search');
    if (resourceTypes) predictiveSearchUrl.searchParams.set('resources[type]', resourceTypes);
    if (resultsLimit) predictiveSearchUrl.searchParams.set('resources[limit]', String(resultsLimit));
    if (this.isNativeSearch) predictiveSearchUrl.searchParams.set('resources[limit_scope]', 'each');

    fetch(predictiveSearchUrl.toString(), {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        this.renderedRequestId = requestId;
        this.renderPendingProductPage(requestId);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.resolve({ cancelled: true });
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getProductSearchResults(searchTerm, queryKey, requestId, page) {
    const resolvedTerm = oeSearchTermResolver(searchTerm);
    if (page > 1) {
      if (this.productPageLoading) return;
      this.productPageLoading = true;
      this.productScroll?.setAttribute('aria-busy', 'true');
    }

    const pageKey = `${queryKey}|products|${page}`;
    if (this.cachedProductPages[pageKey]) {
      this.receiveProductPage(this.cachedProductPages[pageKey], searchTerm, queryKey, requestId, page);
      return;
    }

    const url = new URL(this.input.form.action, window.location.origin);
    const vendorTerms = {
      austroflamm: 'Austroflamm',
      wodtke: 'Wodtke',
      'cera design': 'CERA DESIGN',
      varde: 'Varde Ovne',
      'varde ovne': 'Varde Ovne',
      hitze: 'Hitze',
      thermorossi: 'Thermorossi',
      wamsler: 'Wamsler',
    };
    const vendor = vendorTerms[resolvedTerm.trim().toLowerCase()];
    url.searchParams.set('q', vendor ? `vendor:${vendor}` : resolvedTerm);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', 'oe-mega-search-products');
    if (page > 1) url.searchParams.set('page', String(page));

    fetch(url.toString(), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Product search ${response.status}`);
        return response.text();
      })
      .then((markup) => {
        if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedProductPages[pageKey] = markup;
        });
        this.receiveProductPage(markup, searchTerm, queryKey, requestId, page);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        if (requestId !== this.requestId) return;
        this.productPageLoading = false;
        this.productScroll?.removeAttribute('aria-busy');
        console.warn('Product search could not load.', error);
      });
  }

  receiveProductPage(markup, searchTerm, queryKey, requestId, page) {
    if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
    if (page === 1 && this.renderedRequestId !== requestId) {
      this.pendingProductPage = { markup, searchTerm, queryKey, requestId };
      return;
    }

    const pageNode = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-oe-mega-product-page]');
    if (!pageNode) {
      this.productPageLoading = false;
      this.productScroll?.removeAttribute('aria-busy');
      console.warn('Product search section is missing.');
      return;
    }
    if (page === 1) this.renderFirstProductPage(pageNode, searchTerm, queryKey, requestId);
    else this.appendProductPage(pageNode, page);
  }

  renderPendingProductPage(requestId) {
    if (this.pendingProductPage?.requestId !== requestId) return;
    const pending = this.pendingProductPage;
    this.pendingProductPage = null;
    this.receiveProductPage(pending.markup, pending.searchTerm, pending.queryKey, requestId, 1);
  }

  renderFirstProductPage(pageNode, searchTerm, queryKey, requestId) {
    const mega = this.predictiveSearchResults.querySelector('[data-mega-search]');
    const productArea = mega?.querySelector('.oe-mega-search__products');
    const pageGrid = pageNode.querySelector('.oe-mega-search__product-grid');
    if (!mega || !productArea || !pageGrid) return;
    const pinned = productArea.querySelector('.oe-mega-search__product-grid > li');
    const displayedIds = new Set();
    if (pinned?.dataset.productId && this.matchesProductSearch(pinned, searchTerm)) {
      displayedIds.add(pinned.dataset.productId);
      pageGrid.prepend(pinned);
    }
    pageGrid.querySelectorAll(':scope > li').forEach((item) => {
      const id = item.dataset.productId;
      if (!this.matchesProductSearch(item, searchTerm) || (id && displayedIds.has(id) && item !== pinned)) item.remove();
      else if (id) displayedIds.add(id);
    });
    const scroll = document.createElement('div');
    scroll.className = 'oe-mega-search__product-scroll';
    scroll.dataset.oeMegaProductScroll = '';
    scroll.dataset.totalProducts = pageNode.dataset.total || '0';
    scroll.dataset.totalPages = pageNode.dataset.pages || '1';
    scroll.dataset.loadedPages = '1';
    scroll.append(pageGrid);
    productArea.replaceChildren(scroll);

    this.productScroll = scroll;
    this.displayedProductIds = displayedIds;
    this.productQueryKey = queryKey;
    this.productQuery = searchTerm;
    this.productPages = Math.max(1, parseInt(pageNode.dataset.pages || '1', 10));
    this.nextProductPage = 2;
    this.productPageLoading = false;
    this.appendSupplementalProducts(requestId);
    this.balanceProductCards(pageGrid, searchTerm);
    if (displayedIds.size > 0) mega.classList.remove('oe-mega-search--no-results');

    const total = Math.max(parseInt(pageNode.dataset.total || '0', 10), displayedIds.size);
    const leftCount = mega.querySelectorAll('.oe-mega-search__links li').length;
    const countElement = mega.querySelector('[data-total-results]');
    if (countElement) {
      countElement.dataset.totalResults = String(total + leftCount);
      countElement.textContent = `${total + leftCount} Treffer gefunden.`;
    }

    const inner = mega.querySelector('.oe-mega-search__inner');
    const activeScroll = window.matchMedia('(max-width: 1279px)').matches ? inner : scroll;
    const checkForNextPage = (event) => {
      if (requestId !== this.requestId || this.nextProductPage > this.productPages) return;
      const target = event.currentTarget;
      if (target !== activeScroll) return;
      if (target.scrollTop + target.clientHeight < target.scrollHeight - 450) return;
      this.productCatchup = true;
      this.getProductSearchResults(searchTerm, queryKey, requestId, this.nextProductPage);
    };
    scroll.addEventListener('scroll', checkForNextPage, { passive: true });
    inner?.addEventListener('scroll', checkForNextPage, { passive: true });
    if (activeScroll && activeScroll.scrollHeight <= activeScroll.clientHeight + 450 && this.nextProductPage <= this.productPages) {
      this.productCatchup = true;
      this.getProductSearchResults(searchTerm, queryKey, requestId, this.nextProductPage);
    }
  }

  balanceProductCards(grid, searchTerm) {
    const query = oeSearchTermResolver(searchTerm).trim().toLowerCase();
    const broadQuery = ['kam', 'kami', 'kamin', 'kaminofen', 'pell', 'pellet', 'pelletofen'].includes(query);
    const isCoreProduct = (item) => {
      const title = (item.dataset.productTitle || '').toLowerCase();
      if (!broadQuery) return true;
      return query.startsWith('pell')
        ? /^(pelletofen|hybridofen|pellet.kaminofen)/.test(title)
        : /^(kaminofen|kamineinsatz|elektrokamin|ethanol.kamin|bioethanol.kamin|einbau ethanol.kamin)/.test(title);
    };
    const isScrollableGrid = !!grid.closest('[data-oe-mega-product-scroll]');
    const items = isScrollableGrid
      ? [...grid.children, ...this.deferredRequestProducts]
      : Array.from(grid.children);
    const pricedCore = items.filter((item) => item.dataset.priceRequest !== 'true' && isCoreProduct(item));
    const pricedOther = items.filter((item) => item.dataset.priceRequest !== 'true' && !isCoreProduct(item));
    const requestCore = items.filter((item) => item.dataset.priceRequest === 'true' && isCoreProduct(item));
    const requestOther = items.filter((item) => item.dataset.priceRequest === 'true' && !isCoreProduct(item));

    // Different manufacturers may lead the list when their products have comparable relevance.
    if (pricedCore.length > 2) {
      const seenVendors = new Set();
      const head = [];
      for (const item of pricedCore) {
        const vendor = (item.dataset.productVendor || '').toLowerCase();
        if (vendor && !seenVendors.has(vendor)) {
          head.push(item);
          seenVendors.add(vendor);
        }
        if (head.length === 3) break;
      }
      pricedCore.splice(0, pricedCore.length, ...head, ...pricedCore.filter((item) => !head.includes(item)));
    }

    const priced = [...pricedCore, ...pricedOther];
    const requestGroups = new Map();
    for (const item of [...requestCore, ...requestOther]) {
      const vendor = (item.dataset.productVendor || '').toLowerCase();
      if (!requestGroups.has(vendor)) requestGroups.set(vendor, []);
      requestGroups.get(vendor).push(item);
    }
    const requests = [];
    while (requestGroups.size) {
      for (const [vendor, group] of requestGroups) {
        requests.push(group.shift());
        if (!group.length) requestGroups.delete(vendor);
      }
    }
    const pricedCount = priced.length;
    const ordered = [];
    while (priced.length >= 5) {
      ordered.push(...priced.splice(0, 5));
      if (requests.length) ordered.push(requests.shift());
    }
    ordered.push(...priced);
    if (isScrollableGrid && pricedCount && this.nextProductPage <= this.productPages) {
      this.deferredRequestProducts = requests;
    } else {
      ordered.push(...requests);
      this.deferredRequestProducts = [];
    }
    grid.replaceChildren(...ordered);
  }

  matchesProductSearch(item, searchTerm) {
    const words = oeSearchTermResolver(searchTerm).trim().toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
    const searchable = `${item.dataset.productTitle || ''} ${item.dataset.productVendor || ''} ${item.querySelector('a[href*="/products/"]')?.getAttribute('href') || ''}`.toLocaleLowerCase('de');
    return words.length > 0 && words.every((word) => searchable.includes(word));
  }

  loadSupplementalProducts(searchTerm, requestId) {
    const url = new URL('/search', window.location.origin);
    url.searchParams.set('q', 'pellet');
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'oe-mega-search-products');
    if (this.dataset.oeThemeId) url.searchParams.set('preview_theme_id', this.dataset.oeThemeId);
    const urls = [url, new URL(url)];
    urls[1].searchParams.set('page', '2');
    Promise.all(urls.map((resource) => fetch(resource.toString(), { signal: this.abortController.signal })
      .then((response) => { if (!response.ok) throw new Error(`HTTP ${response.status}`); return response.text(); })
      .catch(() => '')))
      .then((pages) => {
        if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
        const items = [];
        pages.forEach((markup) => {
          const source = new DOMParser().parseFromString(markup, 'text/html');
          source.querySelectorAll('[data-oe-mega-product-page] .oe-mega-search__product-grid > li').forEach((item) => {
            if (item.dataset.priceRequest === 'false' && /^(pelletofen|hybridofen|pellet.kaminofen)/i.test(item.dataset.productTitle || '')) items.push(item);
          });
        });
        this.pendingSupplementalProducts = items;
        this.appendSupplementalProducts(requestId);
      });
  }

  appendSupplementalProducts(requestId) {
    if (requestId !== this.requestId || !this.productScroll || !this.pendingSupplementalProducts) return;
    const grid = this.productScroll.querySelector('.oe-mega-search__product-grid');
    if (!grid) return;
    this.pendingSupplementalProducts.forEach((item) => {
      const id = item.dataset.productId;
      if (!id || this.displayedProductIds.has(id) || !this.matchesProductSearch(item, this.productQuery)) return;
      this.displayedProductIds.add(id);
      grid.append(item);
    });
    this.pendingSupplementalProducts = null;
    this.balanceProductCards(grid, this.productQuery);
  }

  appendProductPage(pageNode, page) {
    const grid = this.productScroll?.querySelector('.oe-mega-search__product-grid');
    if (!grid || page !== this.nextProductPage) return;
    pageNode.querySelectorAll('.oe-mega-search__product-grid > li').forEach((item) => {
      const id = item.dataset.productId;
      if (id && !this.displayedProductIds.has(id) && this.matchesProductSearch(item, this.productQuery)) {
        this.displayedProductIds.add(id);
        grid.append(item);
      }
    });
    this.nextProductPage = page + 1;
    this.balanceProductCards(grid, this.productQuery);
    this.productScroll.dataset.loadedPages = String(page);
    this.productPageLoading = false;
    this.productScroll.removeAttribute('aria-busy');
    const inner = this.productScroll.closest('.oe-mega-search__inner');
    const activeScroll = window.matchMedia('(max-width: 1279px)').matches ? inner : this.productScroll;
    const stillNearEnd = activeScroll && activeScroll.scrollTop + activeScroll.clientHeight >= activeScroll.scrollHeight - 450;
    if (this.productCatchup && stillNearEnd && this.nextProductPage <= this.productPages) {
      this.getProductSearchResults(this.productQuery, this.productQueryKey, this.requestId, this.nextProductPage);
    } else {
      this.productCatchup = false;
    }
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    if (this.isNativeSearch) {
      const initialGrid = this.predictiveSearchResults.querySelector('.oe-mega-search__product-grid');
      if (initialGrid) {
        initialGrid.querySelectorAll(':scope > li').forEach((item) => {
          if (!this.matchesProductSearch(item, this.getQuery())) item.remove();
        });
        this.balanceProductCards(initialGrid, this.getQuery());
      }
    }
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.isNativeSearch) {
      this.updateOverlayPosition();
      document.documentElement.classList.add('oe-search-open');
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  updateOverlayPosition() {
    const headerRect = this.closest('.section-header')?.getBoundingClientRect();
    const scrollbarWidth = Math.max(0, Math.round(window.innerWidth - (headerRect?.width || window.innerWidth)));
    document.documentElement.style.setProperty('--oe-search-scrollbar-width', `${scrollbarWidth}px`);
    this.predictiveSearchResults.style.setProperty('--oe-search-overlay-top', `${Math.max(0, Math.round(headerRect?.bottom || 0))}px`);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeAndFocusInput() {
    this.suppressFocusOpen = true;
    this.close();
    this.input.focus();
    window.setTimeout(() => { this.suppressFocusOpen = false; }, 0);
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    if (this.isNativeSearch && !document.querySelector('predictive-search[data-native-predictive-search][open]')) {
      document.documentElement.classList.remove('oe-search-open');
      document.documentElement.style.removeProperty('--oe-search-scrollbar-width');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
